import ListCard from 'components/ListCard/ListCard'
import ListCardGroup from 'components/ListCard/ListCardGroup'
import { PaymentFeeDto } from '../../../generated-api'

type PaymentFeeListProps = {
    paymentFees: PaymentFeeDto[]
    onFeeClick: (fee: PaymentFeeDto) => void
}

const PaymentFeeList = (props: PaymentFeeListProps) => {
    const { paymentFees, onFeeClick } = props

    // Helper to check if fee is historic (end-dated)
    const isHistoric = (fee: PaymentFeeDto): boolean => {
        return fee.activeTo !== null && fee.activeTo !== undefined
    }

    // Separate active and historic fees
    const activeFees = paymentFees.filter((pf) => !isHistoric(pf))
    const historicFees = paymentFees.filter((pf) => isHistoric(pf))

    // Sort function
    const sortFees = (fees: PaymentFeeDto[]) => {
        return fees.sort((a, b) => {
            if (a.paymentProvider !== b.paymentProvider) {
                return a.paymentProvider.localeCompare(b.paymentProvider)
            }
            if (a.paymentMethod !== b.paymentMethod) {
                return a.paymentMethod.localeCompare(b.paymentMethod)
            }
            if (a.locationId !== b.locationId) {
                return (a.locationId || 0) - (b.locationId || 0)
            }
            return a.currency.localeCompare(b.currency)
        })
    }

    const sortedActiveFees = sortFees(activeFees)
    const sortedHistoricFees = sortFees(historicFees)

    const renderFeeCard = (fee: PaymentFeeDto, index: number, historic: boolean) => (
        <ListCard
            key={`${fee.paymentProvider}-${fee.paymentMethod}-${fee.currency}-${fee.locationId}-${index}`}
            className={`flex flex-col gap-2 cursor-pointer hover:bg-grey-lightest ${
                historic ? 'opacity-60 border-l-4 border-grey-dark' : ''
            }`}
            onClick={() => onFeeClick(fee)}
        >
            <div className="flex items-center justify-between">
                <div>
                    <h4 className="font-medium">
                        {fee.paymentProvider} - {fee.paymentMethod}
                        {historic && <span className="ml-2 text-small text-grey-dark">(Historic)</span>}
                    </h4>
                    <div className="text-small text-grey-dark">
                        Currency: {fee.currency}
                        {fee.locationId && ` • Location ID: ${fee.locationId}`}
                        {!fee.locationId && ' • Global'}
                    </div>
                    {historic && fee.activeTo && (
                        <div className="text-small text-grey-dark">
                            Ended: {new Date(fee.activeTo).toLocaleDateString()}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex gap-4 text-small">
                <div>
                    <span className="text-grey-dark">Fixed Fee:</span>
                    <span className="ml-1 font-medium">{fee.fixedFeeAmount.toString()}</span>
                </div>
                <div>
                    <span className="text-grey-dark">Variable Fee:</span>
                    <span className="ml-1 font-medium">{fee.variableFeePercentage.toString()}%</span>
                </div>
            </div>

            {fee.cardSchemePriceOverrides && fee.cardSchemePriceOverrides.length > 0 && (
                <div>
                    <div className="text-small text-grey-dark mb-1">Card Scheme Overrides:</div>
                    <div className="flex flex-col gap-1">
                        {fee.cardSchemePriceOverrides.map((override) => (
                            <div key={override.cardScheme} className="text-small bg-grey-lightest p-2 rounded">
                                <span className="font-medium">{override.cardScheme}:</span>
                                <span className="ml-2">Fixed: {override.fixedFeeAmount.toString()}</span>
                                <span className="ml-2">Variable: {override.variableFeePercentage.toString()}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </ListCard>
    )

    return (
        <ListCardGroup>
            {sortedActiveFees.length > 0 && (
                <>{sortedActiveFees.map((fee, index) => renderFeeCard(fee, index, false))}</>
            )}

            {sortedHistoricFees.length > 0 && (
                <>
                    <div className="mt-6 mb-2 text-grey-dark font-medium">Historic Fees</div>
                    {sortedHistoricFees.map((fee, index) => renderFeeCard(fee, index, true))}
                </>
            )}

            {sortedActiveFees.length === 0 && sortedHistoricFees.length === 0 && (
                <div className="text-grey-dark p-4">No fees found</div>
            )}
        </ListCardGroup>
    )
}

export default PaymentFeeList
